// FilterBar component with sliding drawer for filters
import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useSettings } from '../contexts/SettingsContext';
import { CatchFilters, SpeciesWithTranslation, SortOption, GroupOption } from '../entities/CatchesPageTypes';
import { FishingArea } from '../entities/FishingEntities';
import { SavedFilter } from '../entities/SavedFilter';
import * as SavedFilterService from '../services/SavedFilterService';
import { getFilterOptions } from '../services/CatchFilterService';
import { 
  convertTemperature, 
  convertWindSpeed, 
  convertPressure,
  convertWeight,
  convertLength,
  getTemperatureUnit,
  getWindSpeedUnit,
  getPressureUnit,
  getWeightUnit,
  getLengthUnit
} from '../utils/unitConversion';

interface FilterBarProps {
  filters: CatchFilters;
  onFilterChange: (filters: CatchFilters) => void;
  onClear: () => void;
  areas: FishingArea[];
  species: SpeciesWithTranslation[];
  sortBy: SortOption;
  onSortChange: (sort: SortOption) => void;
  groupBy: GroupOption;
  onGroupChange: (group: GroupOption) => void;
  maxWeight: number;
  maxLength: number;
  currentFilterId?: string | null; // Currently applied saved filter ID
  onFilterSelect: (filterId: string | null, filters: CatchFilters) => void; // Callback when filter selected
}

export const FilterBar: React.FC<FilterBarProps> = ({
  filters,
  onFilterChange,
  onClear,
  areas,
  species,
  sortBy,
  onSortChange,
  groupBy,
  onGroupChange,
  maxWeight,
  maxLength,
  currentFilterId,
  onFilterSelect
}) => {
  const { t } = useLanguage();
  const { unitSystem } = useSettings();
  const [showFilters, setShowFilters] = useState(false);
  const [savedFilters, setSavedFilters] = useState<SavedFilter[]>([]);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [filterName, setFilterName] = useState('');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  
  // Available methods and baits from database
  const [availableMethods, setAvailableMethods] = useState<string[]>([]);
  const [availableBaits, setAvailableBaits] = useState<string[]>([]);
  
  const activeFilterCount = 
    (filters.search ? 1 : 0) +
    (filters.dateRange ? 1 : 0) +
    (filters.species.length > 0 ? 1 : 0) +
    (filters.areas.length > 0 ? 1 : 0) +
    (filters.caughtOnly !== null ? 1 : 0) +
    (filters.minWeight !== undefined ? 1 : 0) +
    (filters.maxWeight !== undefined ? 1 : 0) +
    (filters.minLength !== undefined ? 1 : 0) +
    (filters.maxLength !== undefined ? 1 : 0) +
    (filters.methods.length > 0 ? 1 : 0) +
    (filters.baits.length > 0 ? 1 : 0) +
    (filters.seasons.length > 0 ? 1 : 0) +
    (filters.timeOfDay ? 1 : 0) +
    (filters.moonPhase ? 1 : 0) +
    (filters.windDirections.length > 0 ? 1 : 0);
  
  // Load saved filters on mount
  useEffect(() => {
    const loadSavedFilters = async () => {
      const filters = await SavedFilterService.getAllSavedFilters();
      setSavedFilters(filters);
    };
    loadSavedFilters();
  }, []);
  
  // Load available methods and baits from database
  useEffect(() => {
    const loadFilterOptions = async () => {
      const options = await getFilterOptions();
      setAvailableMethods(options.methods);
      setAvailableBaits(options.baits);
    };
    loadFilterOptions();
  }, []);
  
  // Get current filter name for display
  const currentFilterName = currentFilterId 
    ? savedFilters.find(f => f.id === currentFilterId)?.name || t.allCatches
    : t.allCatches;
  
  // Local state for sliders (to avoid too many updates)
  const [weightRange, setWeightRange] = useState({ min: filters.minWeight || 0, max: filters.maxWeight || maxWeight });
  const [lengthRange, setLengthRange] = useState({ min: filters.minLength || 0, max: filters.maxLength || maxLength });
  
  // Update local state when filters change externally (e.g., clear filters)
  useEffect(() => {
    setWeightRange({ 
      min: filters.minWeight || 0, 
      max: filters.maxWeight || maxWeight 
    });
  }, [filters.minWeight, filters.maxWeight, maxWeight]);
  
  useEffect(() => {
    setLengthRange({ 
      min: filters.minLength || 0, 
      max: filters.maxLength || maxLength 
    });
  }, [filters.minLength, filters.maxLength, maxLength]);
  
  // Lock body scroll when drawer is open
  useEffect(() => {
    if (showFilters) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [showFilters]);
  
  // Close on ESC key
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showFilters) setShowFilters(false);
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [showFilters]);
  
  return (
    <>
      {/* Filter Dropdown + Edit Button */}
      <div className="flex gap-2 p-3 bg-white/5 backdrop-blur-xl border-b border-white/10">
        {/* Saved Filter Dropdown */}
        <div className="relative flex-1">
          <button
            onClick={() => setShowFilterDropdown(!showFilterDropdown)}
            className="w-full flex items-center justify-between px-4 py-2.5 bg-white/8 border border-white/20 rounded-lg text-white hover:bg-white/12 transition-all"
          >
            <span className="flex items-center gap-2">
              <span className="text-lg">
                {currentFilterId 
                  ? savedFilters.find(f => f.id === currentFilterId)?.icon || '🎣'
                  : '🎣'}
              </span>
              <span className="font-medium truncate">{currentFilterName}</span>
              {activeFilterCount > 0 && (
                <span className="bg-cyan-500/30 text-cyan-300 px-2 py-0.5 rounded-full text-xs">
                  {activeFilterCount}
                </span>
              )}
            </span>
            <svg className={`w-5 h-5 transition-transform ${showFilterDropdown ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          
          {/* Dropdown Menu */}
          {showFilterDropdown && (
            <>
              <div className="fixed inset-0 z-[100]" onClick={() => setShowFilterDropdown(false)} />
              <div className="absolute top-full left-0 right-0 mt-2 bg-slate-900/95 backdrop-blur-xl border border-white/10 rounded-lg shadow-2xl z-[101] max-h-80 overflow-y-auto">
                {/* All Catches (default) */}
                <button
                  onClick={() => {
                    onFilterSelect(null, SavedFilterService.getDefaultAllCatchesFilter());
                    setShowFilterDropdown(false);
                  }}
                  className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-white/5 transition-colors border-b border-white/5 ${!currentFilterId ? 'bg-cyan-500/10' : ''}`}
                >
                  <span className="text-xl">🎣</span>
                  <span className="flex-1 text-left text-white">{t.allCatches || 'All Catches'}</span>
                  {!currentFilterId && <span className="text-cyan-300">✓</span>}
                </button>
                
                {/* Saved Filters */}
                {savedFilters.map(filter => (
                  <button
                    key={filter.id}
                    onClick={async () => {
                      onFilterSelect(filter.id, filter.filters);
                      await SavedFilterService.markFilterAsUsed(filter.id);
                      setShowFilterDropdown(false);
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-white/5 transition-colors border-b border-white/5 ${currentFilterId === filter.id ? 'bg-cyan-500/10' : ''}`}
                  >
                    <span className="text-xl">{filter.icon || '📁'}</span>
                    <span className="flex-1 text-left text-white">{filter.name}</span>
                    {currentFilterId === filter.id && <span className="text-cyan-300">✓</span>}
                  </button>
                ))}
                
                {/* Create New Filter */}
                <button
                  onClick={() => {
                    setShowFilterDropdown(false);
                    setShowFilters(true); // Open drawer to configure new filter
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/5 transition-colors text-cyan-300 font-medium"
                >
                  <span className="text-xl">➕</span>
                  <span>{t.createNewFilter || '+ Create New Filter'}</span>
                </button>
              </div>
            </>
          )}
        </div>
        
        {/* Edit/Configure Filter Button */}
        <button
          onClick={() => setShowFilters(true)}
          className="px-4 py-2.5 bg-cyan-500/20 text-cyan-300 border border-cyan-500/50 rounded-lg hover:bg-cyan-500/30 transition-all flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
          </svg>
          <span className="hidden sm:inline">{t.configure || 'Configure'}</span>
        </button>
      </div>
      
      {/* Sliding Drawer - Backdrop */}
      <div 
        className={`fixed inset-0 bg-black/70 backdrop-blur-sm transition-opacity duration-300 ${
          showFilters ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        style={{ zIndex: 9999 }}
        onClick={() => setShowFilters(false)}
      />
      
      {/* Sliding Drawer - Panel */}
      <div 
        className={`fixed right-0 top-0 bottom-0 w-full sm:w-96 bg-slate-900/95 backdrop-blur-xl border-l border-white/10 shadow-2xl transform transition-transform duration-300 ease-out overflow-y-auto ${
          showFilters ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{ zIndex: 10000 }}
      >
        {/* Drawer Header */}
        <div className="sticky top-0 bg-slate-900/95 backdrop-blur-xl border-b border-white/10 p-4 flex items-center justify-between z-10">
          <h2 className="text-xl font-semibold text-cyan-300 flex items-center gap-2">
            🔍 {t.filters}
            {activeFilterCount > 0 && (
              <span className="bg-cyan-500/30 text-cyan-300 px-2 py-0.5 rounded-full text-sm">
                {activeFilterCount}
              </span>
            )}
          </h2>
          <button
            onClick={() => setShowFilters(false)}
            className="text-white/60 hover:text-white transition-colors p-2 hover:bg-white/5 rounded-lg"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Drawer Content - Scrollable */}
        <div className="p-4 space-y-6 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 140px)' }}>
          {/* Help Text for Save Workflow */}
          {activeFilterCount > 0 && !currentFilterId && (
            <div className="bg-gradient-to-r from-green-500/10 to-cyan-500/10 border border-green-500/30 rounded-xl p-4 mb-4">
              <div className="flex items-start gap-3">
                <span className="text-2xl mt-0.5">💡</span>
                <div className="flex-1">
                  <p className="text-sm text-white/90 font-medium mb-1">
                    Spara dina filter för återanvändning
                  </p>
                  <p className="text-xs text-white/70">
                    Justera filter nedan och klicka &quot;💾 {t.saveFilter}&quot; för att spara inställningarna. Du kan sedan snabbt återanvända dem från dropdown-menyn.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {currentFilterId && (
            <div className="bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/30 rounded-xl p-4 mb-4">
              <div className="flex items-start gap-3">
                <span className="text-2xl mt-0.5">✏️</span>
                <div className="flex-1">
                  <p className="text-sm text-white/90 font-medium mb-1">
                    Redigerar sparat filter
                  </p>
                  <p className="text-xs text-white/70">
                    Gör ändringar och klicka &quot;💾 {t.updateFilter || 'Uppdatera filter'}&quot; för att spara eller &quot;🗑️ {t.deleteFilter || 'Ta bort'}&quot; för att radera filtret.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {/* Date Range Filter */}
          <div>
            <label className="block text-sm font-semibold text-white/90 mb-2.5">
              📅 {t.date}
            </label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { value: '', label: t.allDates },
                { value: 'today', label: t.today },
                { value: 'week', label: t.thisWeek },
                { value: 'month', label: t.thisMonth },
                { value: 'year', label: t.thisYear }
              ].map(option => (
                <button
                  key={option.value}
                  onClick={() => onFilterChange({ ...filters, dateRange: option.value as any })}
                  className={`px-3 py-2.5 rounded-lg border-2 transition-all text-sm font-medium ${
                    filters.dateRange === option.value
                      ? 'bg-cyan-500/30 text-cyan-300 border-cyan-500/60 shadow-lg shadow-cyan-500/20'
                      : 'bg-white/5 text-white border-white/10 hover:bg-white/10'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
          
          {/* Species Filter */}
          <div>
            <label className="block text-sm font-semibold text-white/90 mb-2.5">
              🐟 {t.species}
            </label>
            <div className="grid grid-cols-2 gap-2">
              {species.map(s => (
                <button
                  key={s.key}
                  onClick={() => {
                    const isSelected = filters.species.includes(s.key);
                    onFilterChange({
                      ...filters,
                      species: isSelected 
                        ? filters.species.filter(sp => sp !== s.key)
                        : [...filters.species, s.key]
                    });
                  }}
                  className={`px-3 py-2.5 rounded-lg border-2 transition-all text-sm font-medium flex items-center justify-between ${
                    filters.species.includes(s.key)
                      ? 'bg-cyan-500/30 text-cyan-300 border-cyan-500/60 shadow-lg shadow-cyan-500/20'
                      : 'bg-white/5 text-white border-white/10 hover:bg-white/10'
                  }`}
                >
                  <span>{s.name}</span>
                  {filters.species.includes(s.key) && <span className="text-cyan-300">✓</span>}
                </button>
              ))}
            </div>
          </div>
          
          {/* Areas Filter */}
          {areas.length > 0 && (
            <div>
              <label className="block text-sm font-semibold text-white/90 mb-2.5">
                📍 {t.area}
              </label>
              <div className="grid grid-cols-2 gap-2">
                {areas.map(area => (
                  <button
                    key={area.id}
                    onClick={() => {
                      const isSelected = filters.areas.includes(area.id);
                      onFilterChange({
                        ...filters,
                        areas: isSelected 
                          ? filters.areas.filter(a => a !== area.id)
                          : [...filters.areas, area.id]
                      });
                    }}
                    className={`px-3 py-2.5 rounded-lg border-2 transition-all text-sm font-medium flex items-center justify-between ${
                      filters.areas.includes(area.id)
                        ? 'bg-cyan-500/30 text-cyan-300 border-cyan-500/60 shadow-lg shadow-cyan-500/20'
                        : 'bg-white/5 text-white border-white/10 hover:bg-white/10'
                    }`}
                  >
                    <span className="truncate">{area.name}</span>
                    {filters.areas.includes(area.id) && <span className="text-cyan-300 ml-2">✓</span>}
                  </button>
                ))}
              </div>
            </div>
          )}
          
          {/* Caught/Contact Filter */}
          <div>
            <label className="block text-sm font-semibold text-white/90 mb-2.5">
              🎯 {t.catches}
            </label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { value: null, label: t.catches },
                { value: true, label: t.landed },
                { value: false, label: t.contact }
              ].map((option, idx) => (
                <button
                  key={idx}
                  onClick={() => onFilterChange({ ...filters, caughtOnly: option.value })}
                  className={`px-3 py-2.5 rounded-lg border-2 transition-all text-sm font-medium ${
                    filters.caughtOnly === option.value
                      ? 'bg-cyan-500/30 text-cyan-300 border-cyan-500/60 shadow-lg shadow-cyan-500/20'
                      : 'bg-white/5 text-white border-white/10 hover:bg-white/10'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
          
          {/* Weight Range Filter */}
          <div>
            <label className="block text-sm font-semibold text-white/90 mb-2.5">
              ⚖️ {t.weight || 'Vikt'}: {convertWeight(weightRange.min, unitSystem).toFixed(weightRange.min < 1000 && unitSystem === 'imperial' ? 1 : 2)} {getWeightUnit(unitSystem)} - {convertWeight(weightRange.max, unitSystem).toFixed(weightRange.max < 1000 && unitSystem === 'imperial' ? 1 : 2)} {getWeightUnit(unitSystem)}
            </label>
            <div className="relative h-12 flex items-center">
              {/* Track background */}
              <div className="absolute left-0 right-0 h-2 bg-white/10 rounded-lg" style={{ top: '50%', transform: 'translateY(-50%)' }}>
                {/* Active range fill */}
                <div 
                  className="absolute h-full bg-cyan-500 rounded-lg transition-all"
                  style={{
                    left: `${(weightRange.min / maxWeight) * 100}%`,
                    right: `${100 - (weightRange.max / maxWeight) * 100}%`
                  }}
                />
              </div>
              
              {/* Min handle */}
              <input
                type="range"
                min="0"
                max={maxWeight}
                step={Math.max(1, Math.floor(maxWeight / 100))}
                value={weightRange.min}
                onChange={(e) => {
                  const val = parseInt(e.target.value);
                  if (val <= weightRange.max) {
                    setWeightRange(prev => ({ ...prev, min: val }));
                    onFilterChange({ ...filters, minWeight: val > 0 ? val : undefined });
                  }
                }}
                className="absolute w-full appearance-none bg-transparent pointer-events-none"
                style={{ 
                  zIndex: weightRange.min > weightRange.max - 100 ? 5 : 3
                }}
              />
              
              {/* Max handle */}
              <input
                type="range"
                min="0"
                max={maxWeight}
                step={Math.max(1, Math.floor(maxWeight / 100))}
                value={weightRange.max}
                onChange={(e) => {
                  const val = parseInt(e.target.value);
                  if (val >= weightRange.min) {
                    setWeightRange(prev => ({ ...prev, max: val }));
                    onFilterChange({ ...filters, maxWeight: val < maxWeight ? val : undefined });
                  }
                }}
                className="absolute w-full appearance-none bg-transparent pointer-events-none"
                style={{ 
                  zIndex: 4
                }}
              />
            </div>
            
            {/* Labels */}
            <div className="flex justify-between text-xs text-white/60 mt-1">
              <span>0 {getWeightUnit(unitSystem)}</span>
              <span>{convertWeight(maxWeight, unitSystem).toFixed(1)} {getWeightUnit(unitSystem)}</span>
            </div>
          </div>
          
          {/* Length Range Filter */}
          <div>
            <label className="block text-sm font-semibold text-white/90 mb-2.5">
              📏 {t.length || 'Längd'}: {Math.round(convertLength(lengthRange.min, unitSystem))} {getLengthUnit(unitSystem)} - {Math.round(convertLength(lengthRange.max, unitSystem))} {getLengthUnit(unitSystem)}
            </label>
            <div className="relative h-12 flex items-center">
              {/* Track background */}
              <div className="absolute left-0 right-0 h-2 bg-white/10 rounded-lg" style={{ top: '50%', transform: 'translateY(-50%)' }}>
                {/* Active range fill */}
                <div 
                  className="absolute h-full bg-cyan-500 rounded-lg transition-all"
                  style={{
                    left: `${(lengthRange.min / maxLength) * 100}%`,
                    right: `${100 - (lengthRange.max / maxLength) * 100}%`
                  }}
                />
              </div>
              
              {/* Min handle */}
              <input
                type="range"
                min="0"
                max={maxLength}
                step={Math.max(1, Math.floor(maxLength / 150))}
                value={lengthRange.min}
                onChange={(e) => {
                  const val = parseInt(e.target.value);
                  if (val <= lengthRange.max) {
                    setLengthRange(prev => ({ ...prev, min: val }));
                    onFilterChange({ ...filters, minLength: val > 0 ? val : undefined });
                  }
                }}
                className="absolute w-full appearance-none bg-transparent pointer-events-none"
                style={{ 
                  zIndex: lengthRange.min > lengthRange.max - 10 ? 5 : 3
                }}
              />
              
              {/* Max handle */}
              <input
                type="range"
                min="0"
                max={maxLength}
                step={Math.max(1, Math.floor(maxLength / 150))}
                value={lengthRange.max}
                onChange={(e) => {
                  const val = parseInt(e.target.value);
                  if (val >= lengthRange.min) {
                    setLengthRange(prev => ({ ...prev, max: val }));
                    onFilterChange({ ...filters, maxLength: val < maxLength ? val : undefined });
                  }
                }}
                className="absolute w-full appearance-none bg-transparent pointer-events-none"
                style={{ 
                  zIndex: 4
                }}
              />
            </div>
            
            {/* Labels */}
            <div className="flex justify-between text-xs text-white/60 mt-1">
              <span>0 {getLengthUnit(unitSystem)}</span>
              <span>{Math.round(convertLength(maxLength, unitSystem))} {getLengthUnit(unitSystem)}</span>
            </div>
          </div>
          
          {/* Methods Filter */}
          <div>
            <label className="block text-sm font-semibold text-white/90 mb-2.5">
              🎣 {t.methods || 'Metoder'}
            </label>
            {availableMethods.length > 0 ? (
              <div className="grid grid-cols-2 gap-2">
                {availableMethods.map(methodId => {
                  // Map method ID to translation key: "spin" -> "methodSpin"
                  const translationKey = `method${methodId.charAt(0).toUpperCase() + methodId.slice(1)}` as keyof typeof t;
                  const displayName = (t[translationKey] as string) || methodId;
                  
                  return (
                    <button
                      key={methodId}
                      onClick={() => {
                        const methods = filters.methods.includes(methodId)
                          ? filters.methods.filter(m => m !== methodId)
                          : [...filters.methods, methodId];
                        onFilterChange({ ...filters, methods });
                      }}
                      className={`px-3 py-2.5 rounded-lg border-2 transition-all text-sm font-medium ${
                        filters.methods.includes(methodId)
                          ? 'bg-cyan-500/30 text-cyan-300 border-cyan-500/60 shadow-lg shadow-cyan-500/20'
                          : 'bg-white/5 text-white border-white/10 hover:bg-white/10'
                      }`}
                    >
                      {displayName}
                      {filters.methods.includes(methodId) && <span className="ml-2">✓</span>}
                    </button>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-white/50 py-4 text-center">
                {t.noCatchesYet || 'Inga metoder använda ännu'}
              </p>
            )}
          </div>
          
          {/* Baits Filter */}
          {availableBaits.length > 0 && (
            <div>
              <label className="block text-sm font-semibold text-white/90 mb-2.5">
                🪝 {t.baits || 'Beten'}
              </label>
              <div className="grid grid-cols-2 gap-2">
                {availableBaits.map(tackleId => {
                  // Map tackle ID to translation key: "wobbler" -> "tackleWobbler"
                  const translationKey = `tackle${tackleId.charAt(0).toUpperCase() + tackleId.slice(1)}` as keyof typeof t;
                  const displayName = (t[translationKey] as string) || tackleId;
                  
                  return (
                    <button
                      key={tackleId}
                      onClick={() => {
                        const baits = filters.baits.includes(tackleId)
                          ? filters.baits.filter(b => b !== tackleId)
                          : [...filters.baits, tackleId];
                        onFilterChange({ ...filters, baits });
                      }}
                      className={`px-3 py-2.5 rounded-lg border-2 transition-all text-sm font-medium ${
                        filters.baits.includes(tackleId)
                          ? 'bg-cyan-500/30 text-cyan-300 border-cyan-500/60 shadow-lg shadow-cyan-500/20'
                          : 'bg-white/5 text-white border-white/10 hover:bg-white/10'
                      }`}
                    >
                      {displayName} {filters.baits.includes(tackleId) && <span className="ml-2">✓</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          
          {/* Seasons Filter */}
          <div>
            <label className="block text-sm font-semibold text-white/90 mb-2.5">
              🌍 {t.seasons || 'Årstider'}
            </label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { value: 'spring', label: t.spring || 'Vår', icon: '🌸' },
                { value: 'summer', label: t.summer || 'Sommar', icon: '☀️' },
                { value: 'fall', label: t.fall || 'Höst', icon: '🍂' },
                { value: 'winter', label: t.winter || 'Vinter', icon: '❄️' }
              ].map(season => (
                <button
                  key={season.value}
                  onClick={() => {
                    const seasons = filters.seasons.includes(season.value)
                      ? filters.seasons.filter(s => s !== season.value)
                      : [...filters.seasons, season.value];
                    onFilterChange({ ...filters, seasons });
                  }}
                  className={`px-3 py-2.5 rounded-lg border-2 transition-all text-sm font-medium flex items-center justify-center gap-2 ${
                    filters.seasons.includes(season.value)
                      ? 'bg-cyan-500/30 text-cyan-300 border-cyan-500/60 shadow-lg shadow-cyan-500/20'
                      : 'bg-white/5 text-white border-white/10 hover:bg-white/10'
                  }`}
                >
                  <span>{season.icon}</span>
                  <span>{season.label}</span>
                  {filters.seasons.includes(season.value) && <span>✓</span>}
                </button>
              ))}
            </div>
          </div>
          
          {/* Time of Day Filter */}
          {filters.timeOfDay && (
            <div>
              <label className="block text-sm font-semibold text-white/90 mb-2.5">
                🕐 {t.timeOfDay || 'Tid på dagen'}: {String(filters.timeOfDay.start).padStart(2, '0')}:00 - {String(filters.timeOfDay.end).padStart(2, '0')}:00
              </label>
              <div className="grid grid-cols-4 gap-2 mb-3">
                {[
                  { label: t.morning || 'Morgon', start: 6, end: 12 },
                  { label: t.afternoon || 'Eftermiddag', start: 12, end: 18 },
                  { label: t.evening || 'Kväll', start: 18, end: 24 },
                  { label: t.night || 'Natt', start: 0, end: 6 }
                ].map(preset => (
                  <button
                    key={preset.label}
                    onClick={() => onFilterChange({ ...filters, timeOfDay: { start: preset.start, end: preset.end } })}
                    className="px-2 py-1.5 rounded-lg bg-white/5 text-white/70 text-xs border border-white/10 hover:bg-white/10 transition-all"
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
              <div className="relative h-12 flex items-center">
                <div className="absolute left-0 right-0 h-2 bg-white/10 rounded-lg" style={{ top: '50%', transform: 'translateY(-50%)' }}>
                  <div 
                    className="absolute h-full bg-cyan-500 rounded-lg transition-all"
                    style={{
                      left: `${(filters.timeOfDay.start / 24) * 100}%`,
                      width: `${((filters.timeOfDay.end - filters.timeOfDay.start) / 24) * 100}%`
                    }}
                  />
                </div>
                <input
                  type="range"
                  min="0"
                  max="23"
                  value={filters.timeOfDay.start}
                  onChange={(e) => {
                    const val = parseInt(e.target.value);
                    if (val < filters.timeOfDay!.end) {
                      onFilterChange({ ...filters, timeOfDay: { ...filters.timeOfDay!, start: val } });
                    }
                  }}
                  className="absolute w-full appearance-none bg-transparent pointer-events-none"
                  style={{ zIndex: 5 }}
                />
                <input
                  type="range"
                  min="0"
                  max="23"
                  value={filters.timeOfDay.end}
                  onChange={(e) => {
                    const val = parseInt(e.target.value);
                    if (val > filters.timeOfDay!.start) {
                      onFilterChange({ ...filters, timeOfDay: { ...filters.timeOfDay!, end: val } });
                    }
                  }}
                  className="absolute w-full appearance-none bg-transparent pointer-events-none"
                  style={{ zIndex: 4 }}
                />
              </div>
              <button
                onClick={() => onFilterChange({ ...filters, timeOfDay: undefined })}
                className="mt-2 text-sm text-red-300 hover:text-red-200"
              >
                ✕ Ta bort tidsfilter
              </button>
            </div>
          )}
          {!filters.timeOfDay && (
            <div>
              <button
                onClick={() => onFilterChange({ ...filters, timeOfDay: { start: 0, end: 23 } })}
                className="w-full px-3 py-2.5 rounded-lg border-2 bg-white/5 text-white border-white/10 hover:bg-white/10 transition-all text-sm font-medium"
              >
                🕐 {t.timeOfDay || 'Lägg till tidsfilter'}
              </button>
            </div>
          )}
          
          {/* Moon Phase Filter */}
          {filters.moonPhase && (
            <div>
              <label className="block text-sm font-semibold text-white/90 mb-2.5">
                🌙 {t.moonPhase || 'Månfas'}: {(filters.moonPhase.min * 100).toFixed(0)}% - {(filters.moonPhase.max * 100).toFixed(0)}%
              </label>
              <div className="relative h-12 flex items-center">
                <div className="absolute left-0 right-0 h-2 bg-white/10 rounded-lg" style={{ top: '50%', transform: 'translateY(-50%)' }}>
                  <div 
                    className="absolute h-full bg-cyan-500 rounded-lg transition-all"
                    style={{
                      left: `${filters.moonPhase.min * 100}%`,
                      width: `${(filters.moonPhase.max - filters.moonPhase.min) * 100}%`
                    }}
                  />
                </div>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={filters.moonPhase.min}
                  onChange={(e) => {
                    const val = parseFloat(e.target.value);
                    if (val < filters.moonPhase!.max) {
                      onFilterChange({ ...filters, moonPhase: { ...filters.moonPhase!, min: val } });
                    }
                  }}
                  className="absolute w-full appearance-none bg-transparent pointer-events-none"
                  style={{ zIndex: 5 }}
                />
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={filters.moonPhase.max}
                  onChange={(e) => {
                    const val = parseFloat(e.target.value);
                    if (val > filters.moonPhase!.min) {
                      onFilterChange({ ...filters, moonPhase: { ...filters.moonPhase!, max: val } });
                    }
                  }}
                  className="absolute w-full appearance-none bg-transparent pointer-events-none"
                  style={{ zIndex: 4 }}
                />
              </div>
              <div className="flex justify-between text-xs text-white/60 mt-1">
                <span>🌑 Nymåne</span>
                <span>🌕 Fullmåne</span>
              </div>
              <button
                onClick={() => onFilterChange({ ...filters, moonPhase: undefined })}
                className="mt-2 text-sm text-red-300 hover:text-red-200"
              >
                ✕ Ta bort månfasfilter
              </button>
            </div>
          )}
          {!filters.moonPhase && (
            <div>
              <button
                onClick={() => onFilterChange({ ...filters, moonPhase: { min: 0, max: 1 } })}
                className="w-full px-3 py-2.5 rounded-lg border-2 bg-white/5 text-white border-white/10 hover:bg-white/10 transition-all text-sm font-medium"
              >
                🌙 {t.moonPhase || 'Lägg till månfasfilter'}
              </button>
            </div>
          )}
          
          {/* Weather Filters */}
          <div className="space-y-6 p-4 bg-gradient-to-br from-slate-800/40 to-slate-900/40 backdrop-blur-sm rounded-xl border border-white/10">
            <div className="flex items-center gap-2 mb-4">
              <span className="text-2xl">🌦️</span>
              <label className="text-base font-bold text-white">
                {t.weatherFilters || 'Väderfilter'}
              </label>
            </div>
            
            {/* Temperature */}
            <div className="p-3 bg-white/5 rounded-lg border border-white/5 hover:bg-white/8 transition-all">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-lg">🌡️</span>
                  <span className="text-sm font-medium text-white/90">{t.temperature}</span>
                </div>
                <span className="text-sm font-mono text-cyan-300 bg-cyan-500/20 px-2 py-1 rounded">
                  {convertTemperature(filters.minTemp ?? -20, unitSystem).toFixed(0)}{getTemperatureUnit(unitSystem)} - {convertTemperature(filters.maxTemp ?? 40, unitSystem).toFixed(0)}{getTemperatureUnit(unitSystem)}
                </span>
              </div>
              <div className="relative h-3 bg-white/10 rounded-full shadow-inner">
                {/* Track fill */}
                <div 
                  className="absolute h-full bg-gradient-to-r from-blue-500/60 via-cyan-500/60 to-orange-500/60 rounded-full shadow-lg"
                  style={{
                    left: `${((filters.minTemp ?? -20) + 20) / 60 * 100}%`,
                    right: `${100 - ((filters.maxTemp ?? 40) + 20) / 60 * 100}%`
                  }}
                />
                
                {/* Min slider */}
                <input
                  type="range"
                  min="-20"
                  max="40"
                  step="1"
                  value={filters.minTemp ?? -20}
                  onChange={(e) => {
                    const val = parseFloat(e.target.value);
                    if (val < (filters.maxTemp ?? 40)) {
                      onFilterChange({ ...filters, minTemp: val });
                    }
                  }}
                  className="absolute w-full appearance-none bg-transparent pointer-events-auto cursor-pointer"
                  style={{ zIndex: 3, height: '100%' }}
                />
                
                {/* Max slider */}
                <input
                  type="range"
                  min="-20"
                  max="40"
                  step="1"
                  value={filters.maxTemp ?? 40}
                  onChange={(e) => {
                    const val = parseFloat(e.target.value);
                    if (val > (filters.minTemp ?? -20)) {
                      onFilterChange({ ...filters, maxTemp: val });
                    }
                  }}
                  className="absolute w-full appearance-none bg-transparent pointer-events-auto cursor-pointer"
                  style={{ zIndex: 4, height: '100%' }}
                />
              </div>
              <div className="flex justify-between text-xs text-white/50 mt-2">
                <span>❄️ {convertTemperature(-20, unitSystem).toFixed(0)}{getTemperatureUnit(unitSystem)}</span>
                <span>🔥 {convertTemperature(40, unitSystem).toFixed(0)}{getTemperatureUnit(unitSystem)}</span>
              </div>
            </div>
            
            {/* Wind Speed */}
            <div className="p-3 bg-white/5 rounded-lg border border-white/5 hover:bg-white/8 transition-all">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-lg">💨</span>
                  <span className="text-sm font-medium text-white/90">{t.windSpeed}</span>
                </div>
                <span className="text-sm font-mono text-cyan-300 bg-cyan-500/20 px-2 py-1 rounded">
                  {convertWindSpeed(filters.minWindSpeed ?? 0, unitSystem).toFixed(1)} - {convertWindSpeed(filters.maxWindSpeed ?? 30, unitSystem).toFixed(1)} {getWindSpeedUnit(unitSystem)}
                </span>
              </div>
              <div className="relative h-3 bg-white/10 rounded-full shadow-inner">
                {/* Track fill */}
                <div 
                  className="absolute h-full bg-gradient-to-r from-green-500/60 via-yellow-500/60 to-red-500/60 rounded-full shadow-lg"
                  style={{
                    left: `${(filters.minWindSpeed ?? 0) / 30 * 100}%`,
                    right: `${100 - (filters.maxWindSpeed ?? 30) / 30 * 100}%`
                  }}
                />
                
                {/* Min slider */}
                <input
                  type="range"
                  min="0"
                  max="30"
                  step="0.5"
                  value={filters.minWindSpeed ?? 0}
                  onChange={(e) => {
                    const val = parseFloat(e.target.value);
                    if (val < (filters.maxWindSpeed ?? 30)) {
                      onFilterChange({ ...filters, minWindSpeed: val });
                    }
                  }}
                  className="absolute w-full appearance-none bg-transparent pointer-events-auto cursor-pointer"
                  style={{ zIndex: 3, height: '100%' }}
                />
                
                {/* Max slider */}
                <input
                  type="range"
                  min="0"
                  max="30"
                  step="0.5"
                  value={filters.maxWindSpeed ?? 30}
                  onChange={(e) => {
                    const val = parseFloat(e.target.value);
                    if (val > (filters.minWindSpeed ?? 0)) {
                      onFilterChange({ ...filters, maxWindSpeed: val });
                    }
                  }}
                  className="absolute w-full appearance-none bg-transparent pointer-events-auto cursor-pointer"
                  style={{ zIndex: 4, height: '100%' }}
                />
              </div>
              <div className="flex justify-between text-xs text-white/50 mt-2">
                <span>🍃 {t.calm || 'Lugnt'}</span>
                <span>🌪️ Storm</span>
              </div>
            </div>
            
            {/* Pressure */}
            <div className="p-3 bg-white/5 rounded-lg border border-white/5 hover:bg-white/8 transition-all">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-lg">🌀</span>
                  <span className="text-sm font-medium text-white/90">{t.pressure}</span>
                </div>
                <span className="text-sm font-mono text-cyan-300 bg-cyan-500/20 px-2 py-1 rounded">
                  {convertPressure(filters.minPressure ?? 950, unitSystem).toFixed(unitSystem === 'imperial' ? 2 : 0)} - {convertPressure(filters.maxPressure ?? 1050, unitSystem).toFixed(unitSystem === 'imperial' ? 2 : 0)} {getPressureUnit(unitSystem)}
                </span>
              </div>
              <div className="relative h-3 bg-white/10 rounded-full shadow-inner">
                {/* Track fill */}
                <div 
                  className="absolute h-full bg-gradient-to-r from-purple-500/60 via-cyan-500/60 to-blue-500/60 rounded-full shadow-lg"
                  style={{
                    left: `${((filters.minPressure ?? 950) - 950) / 100 * 100}%`,
                    right: `${100 - ((filters.maxPressure ?? 1050) - 950) / 100 * 100}%`
                  }}
                />
                
                {/* Min slider */}
                <input
                  type="range"
                  min="950"
                  max="1050"
                  step="1"
                  value={filters.minPressure ?? 950}
                  onChange={(e) => {
                    const val = parseFloat(e.target.value);
                    if (val < (filters.maxPressure ?? 1050)) {
                      onFilterChange({ ...filters, minPressure: val });
                    }
                  }}
                  className="absolute w-full appearance-none bg-transparent pointer-events-auto cursor-pointer"
                  style={{ zIndex: 3, height: '100%' }}
                />
                
                {/* Max slider */}
                <input
                  type="range"
                  min="950"
                  max="1050"
                  step="1"
                  value={filters.maxPressure ?? 1050}
                  onChange={(e) => {
                    const val = parseFloat(e.target.value);
                    if (val > (filters.minPressure ?? 950)) {
                      onFilterChange({ ...filters, maxPressure: val });
                    }
                  }}
                  className="absolute w-full appearance-none bg-transparent pointer-events-auto cursor-pointer"
                  style={{ zIndex: 4, height: '100%' }}
                />
              </div>
              <div className="flex justify-between text-xs text-white/50 mt-2">
                <span>↓ Lågtryck</span>
                <span>↑ Högtryck</span>
              </div>
            </div>
            
            {/* Wind Directions */}
            <div className="p-3 bg-white/5 rounded-lg border border-white/5">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-lg">🧭</span>
                <span className="text-sm font-medium text-white/90">
                  {t.windDirections || 'Vindriktningar'}
                </span>
                {filters.windDirections.length > 0 && (
                  <span className="text-xs bg-cyan-500/20 text-cyan-300 px-2 py-0.5 rounded-full ml-auto">
                    {filters.windDirections.length}
                  </span>
                )}
              </div>
              <div className="grid grid-cols-4 gap-2">
                {['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'].map(dir => (
                  <button
                    key={dir}
                    onClick={() => {
                      const directions = filters.windDirections.includes(dir)
                        ? filters.windDirections.filter(d => d !== dir)
                        : [...filters.windDirections, dir];
                      onFilterChange({ ...filters, windDirections: directions });
                    }}
                    className={`px-3 py-2.5 rounded-lg border-2 transition-all text-sm font-bold ${
                      filters.windDirections.includes(dir)
                        ? 'bg-cyan-500/30 text-cyan-300 border-cyan-500/60 shadow-lg shadow-cyan-500/20'
                        : 'bg-white/5 text-white/70 border-white/10 hover:bg-white/10 hover:border-white/20'
                    }`}
                  >
                    {dir}
                  </button>
                ))}
              </div>
            </div>
          </div>
          
          {/* Sort Options */}
          <div>
            <label className="block text-sm font-semibold text-white/90 mb-2.5">
              🔃 {t.sortBy || 'Sortera'}
            </label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { value: 'newest', label: t.newest || 'Nyaste först', icon: '🕐' },
                { value: 'oldest', label: t.oldest || 'Äldsta först', icon: '🕰️' },
                { value: 'weight-desc', label: t.weight || 'Tyngst först', icon: '⚖️' },
                { value: 'length-desc', label: t.length || 'Längst först', icon: '📏' }
              ].map(option => (
                <button
                  key={option.value}
                  onClick={() => onSortChange(option.value as SortOption)}
                  className={`px-3 py-2.5 rounded-lg border-2 transition-all text-sm font-medium flex items-center gap-2 ${
                    sortBy === option.value
                      ? 'bg-cyan-500/30 text-cyan-300 border-cyan-500/60 shadow-lg shadow-cyan-500/20'
                      : 'bg-white/5 text-white border-white/10 hover:bg-white/10'
                  }`}
                >
                  <span>{option.icon}</span>
                  <span className="truncate">{option.label}</span>
                </button>
              ))}
            </div>
          </div>
          
          {/* Group Options */}
          <div>
            <label className="block text-sm font-semibold text-white/90 mb-2.5">
              📂 {t.groupBy || 'Gruppera'}
            </label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { value: 'none', label: t.none || 'Ingen', icon: '—' },
                { value: 'date', label: t.date || 'Datum', icon: '📅' },
                { value: 'species', label: t.species || 'Art', icon: '🐟' },
                { value: 'area', label: t.area || 'Plats', icon: '📍' },
                { value: 'session', label: t.session || 'Pass', icon: '🎣' },
                { value: 'season', label: t.seasons || 'Årstid', icon: '🌍' }
              ].map(option => (
                <button
                  key={option.value}
                  onClick={() => onGroupChange(option.value as GroupOption)}
                  className={`px-3 py-2.5 rounded-lg border-2 transition-all text-sm font-medium flex items-center gap-2 ${
                    groupBy === option.value
                      ? 'bg-cyan-500/30 text-cyan-300 border-cyan-500/60 shadow-lg shadow-cyan-500/20'
                      : 'bg-white/5 text-white border-white/10 hover:bg-white/10'
                  }`}
                >
                  <span>{option.icon}</span>
                  <span className="truncate">{option.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Drawer Footer - Sticky Actions */}
        <div className="sticky bottom-0 bg-slate-900/95 backdrop-blur-xl border-t border-white/10 p-4 space-y-3">
          {/* Filter Management Row */}
          {(currentFilterId || activeFilterCount > 0) && (
            <div className="flex gap-2">
              {currentFilterId ? (
                <>
                  {/* Update existing filter */}
                  <button
                    onClick={async () => {
                      await SavedFilterService.updateSavedFilter(currentFilterId, { filters });
                      const updated = await SavedFilterService.getAllSavedFilters();
                      setSavedFilters(updated);
                    }}
                    className="flex-1 bg-cyan-500/20 text-cyan-300 border-2 border-cyan-500/50 hover:bg-cyan-500/30 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all shadow-lg"
                  >
                    💾 {t.updateFilter || 'Uppdatera filter'}
                  </button>
                  {/* Delete filter */}
                  <button
                    onClick={async () => {
                      if (window.confirm(`Ta bort filtret "${savedFilters.find(f => f.id === currentFilterId)?.name}"?`)) {
                        await SavedFilterService.deleteSavedFilter(currentFilterId);
                        const updated = await SavedFilterService.getAllSavedFilters();
                        setSavedFilters(updated);
                        onFilterSelect(null, SavedFilterService.getDefaultAllCatchesFilter());
                      }
                    }}
                    className="px-4 py-2.5 bg-red-500/20 text-red-300 border-2 border-red-500/50 hover:bg-red-500/30 rounded-lg text-sm font-semibold transition-all shadow-lg"
                  >
                    🗑️ {t.deleteFilter || 'Ta bort'}
                  </button>
                </>
              ) : activeFilterCount > 0 ? (
                /* Save new filter */
                <button
                  onClick={() => setShowSaveModal(true)}
                  className="flex-1 bg-green-500/20 text-green-300 border-2 border-green-500/50 hover:bg-green-500/30 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all shadow-lg"
                >
                  💾 {t.saveFilter || 'Spara filter'}
                </button>
              ) : null}
            </div>
          )}
          
          {/* Action Buttons Row */}
          <div className="flex gap-3">
            {activeFilterCount > 0 && (
              <button
                onClick={() => {
                  onClear();
                  setWeightRange({ min: 0, max: maxWeight });
                  setLengthRange({ min: 0, max: maxLength });
                }}
                className="flex-1 bg-red-500/20 text-red-300 border-2 border-red-500/50 hover:bg-red-500/30 px-4 py-3 rounded-lg font-semibold transition-all shadow-lg shadow-red-500/10"
              >
                🗑️ {t.clearFilters || 'Rensa filter'}
              </button>
            )}
            <button
              onClick={() => setShowFilters(false)}
              className={`${activeFilterCount > 0 ? 'flex-1' : 'w-full'} bg-cyan-500/20 text-cyan-300 border-2 border-cyan-500/50 hover:bg-cyan-500/30 px-4 py-3 rounded-lg font-semibold transition-all shadow-lg shadow-cyan-500/20`}
            >
              ✓ {t.applyFilters || 'Tillämpa filter'}
            </button>
          </div>
        </div>
      </div>
      
      {/* Save Filter Modal */}
      {showSaveModal && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[10001]"
            onClick={() => setShowSaveModal(false)}
          />
          
          {/* Modal */}
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[10002] w-full max-w-md mx-4">
            <div className="bg-slate-900/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl p-6">
              <h3 className="text-xl font-semibold text-cyan-300 mb-4">
                💾 {t.saveFilter}
              </h3>
              
              {/* Filter Name Input */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-white/90 mb-2">
                  {t.filterName}
                </label>
                <input
                  type="text"
                  value={filterName}
                  onChange={(e) => setFilterName(e.target.value)}
                  placeholder="Ex: Gäddfiske vår 2025"
                  className="w-full px-4 py-3 bg-white/8 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50"
                  autoFocus
                />
              </div>
              
              {/* Icon Picker */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-white/90 mb-2">
                  {t.filterIcon}
                </label>
                <div className="grid grid-cols-8 gap-2">
                  {['🎣', '🐟', '🐠', '🐡', '🦈', '🐙', '🦞', '🦀', '🌊', '⛵', '🚤', '⚓', '🎯', '⭐', '💎', '🔥'].map(icon => (
                    <button
                      key={icon}
                      type="button"
                      onClick={() => {
                        const current = filterName;
                        if (!current.startsWith('🎣') && !current.startsWith('🐟')) {
                          setFilterName(icon + ' ' + current);
                        }
                      }}
                      className="text-2xl p-2 bg-white/5 hover:bg-white/10 rounded-lg transition-all border border-white/10 hover:border-cyan-500/50"
                    >
                      {icon}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowSaveModal(false);
                    setFilterName('');
                  }}
                  className="flex-1 px-4 py-3 bg-white/5 text-white/70 border border-white/10 hover:bg-white/10 rounded-lg font-medium transition-all"
                >
                  {t.cancel}
                </button>
                <button
                  onClick={async () => {
                    if (!filterName.trim()) return;
                    
                    const newFilterId = await SavedFilterService.createSavedFilter(
                      filterName.trim(),
                      filters,
                      filterName.split(' ')[0], // First emoji as icon
                      undefined // No color for now
                    );
                    
                    const updated = await SavedFilterService.getAllSavedFilters();
                    setSavedFilters(updated);
                    onFilterSelect(newFilterId, filters);
                    
                    setShowSaveModal(false);
                    setFilterName('');
                  }}
                  disabled={!filterName.trim()}
                  className="flex-1 px-4 py-3 bg-cyan-500/20 text-cyan-300 border border-cyan-500/50 hover:bg-cyan-500/30 rounded-lg font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t.saveFilter}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};
